import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OpenEstimateApiService } from './api/open-estimate.service';
import { CommonDataService } from './common-data.service';
import { MinimumProjectStandardMarginRequestBase, ProjectStandardMargin } from '../models/dtos';
import { CONSTANTS } from '../Constants/Constants';
import { CommonFunctionsService } from './common-functions.service';
import { DecimalPrecisionPipe } from '../pipes/decimal-precision-pipe/decimal-precision.pipe';

@Injectable({
  providedIn: 'root'
})
export class SellAsMinimumMarginService {

  private readonly marginByKey = new Map<string, number>();
  private readonly rangeRowsByKey = new Map<string, ProjectStandardMargin[]>();
  private readonly pendingByKey = new Map<string, Promise<ProjectStandardMargin[]>>();

  constructor(
    private readonly openEstimateApiService: OpenEstimateApiService,
    private readonly commonDataService: CommonDataService,
    private readonly commonFunctionsService: CommonFunctionsService
  ) { }

  get SellAsMinimumMarginValue(): number {
    const request = this.buildMinimumMarginRequest();
    if (!request) {
      return 0;
    }

    const cacheKey = this.getCacheKey(request);
    return this.marginByKey.get(cacheKey) ?? 0;
  }

  async getSellAsMinimumMarginForSellPrice(sellAsSellPrice: number | null): Promise<number> {
    const request = this.buildMinimumMarginRequest();
    if (!request) {
      return 0;
    }

    return this.getMinimumMarginForRequest(request, sellAsSellPrice);
  }

  async getMinimumMarginForRequest(
    request: MinimumProjectStandardMarginRequestBase,
    sellAsSellPrice: number | null
  ): Promise<number> {
    const cacheKey = this.getCacheKey(request);

    const rangeRows = await this.ensureRangeRowsForKey(request, cacheKey);
    const margin = this.resolveMarginFromRange(rangeRows, sellAsSellPrice);
    this.marginByKey.set(cacheKey, margin);
    return margin;
  }

  async preloadForCurrentEstimate(): Promise<void> {
    const request = this.buildMinimumMarginRequest();
    if (!request) {
      return;
    }
    const sellAsSellPrice = this.commonFunctionsService.toNumberOrNull(
      this.commonDataService.getEstimateMetaData()?.estimateTab?.soldASSellPrice
    );
    await this.getMinimumMarginForRequest(request, sellAsSellPrice);
  }

  getWarningTooltipKey(isMarkUpSelected: boolean): string {
    return isMarkUpSelected ? 'MINIMAL_MU_WARNING_TOOLTIP' : 'MINIMAL_GM_WARNING_TOOLTIP';
  }

  getWarningTooltipValue(
    minimumMarginGm: number,
    isMarkUpSelected: boolean,
    precision: number
  ): string {
    const valueToDisplay = isMarkUpSelected
      ? this.convertMinimumGmToMinimumMu(minimumMarginGm, precision)
      : this.roundToPrecision(minimumMarginGm, precision);

    return valueToDisplay.toFixed(precision);
  }

  shouldShowMinimumMarginWarning(reportedMargin: number | null, minimumMargin: number): boolean {
    if (reportedMargin == null) {
      return false;
    }

    const isGlobalPpmMode = this.commonDataService.globalPPMmode;
    const isPpmEstimate = this.commonDataService.getEstimateMetaData()?.estimateSettings?.isPPMEstimate === true;
    // The pipe returns a culture-formatted string (e.g. "12,34" in fr-CA), which Number() cannot parse
    const localReportedMargin: number = this.roundToPrecision(
      reportedMargin,
      this.commonDataService.getDecimalPrecision()
    );

    return (
      !isGlobalPpmMode &&
      !isPpmEstimate &&
      minimumMargin > CONSTANTS.ZERO &&
      localReportedMargin < minimumMargin
    );
  }

  toNumberOrNull(value: unknown): number | null {
    return this.commonFunctionsService.toNumberOrNull(value);
  }

  private convertMinimumGmToMinimumMu(minimumGmPercent: number, precision: number): number {
    const roundedGm = this.roundToPrecision(minimumGmPercent, CONSTANTS.TWO);
    const denominator = CONSTANTS.HUNDRED - roundedGm;
    if (denominator === CONSTANTS.ZERO) {
      return CONSTANTS.ZERO;
    }

    const mu = (roundedGm / denominator) * CONSTANTS.HUNDRED;
    return this.roundToPrecision(mu, precision);
  }

  private roundToPrecision(value: number, precision: number): number {
    const factor = Math.pow(10, precision);
    return Math.round(value * factor) / factor;
  }

  private async ensureRangeRowsForKey(
    request: MinimumProjectStandardMarginRequestBase,
    cacheKey: string
  ): Promise<ProjectStandardMargin[]> {
    const cachedRows = this.rangeRowsByKey.get(cacheKey);
    if (cachedRows) {
      return cachedRows;
    }

    const pendingRequest = this.pendingByKey.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    const requestPromise = this.fetchRangeRows(request, cacheKey);
    this.pendingByKey.set(cacheKey, requestPromise);
    return requestPromise;
  }

  private async fetchRangeRows(
    request: MinimumProjectStandardMarginRequestBase,
    cacheKey: string
  ): Promise<ProjectStandardMargin[]> {
    try {
      const result = await firstValueFrom(
        this.openEstimateApiService.getMinimumProjectStandardMarginRange(request)
      );
      const rows = result?.projectStandardMarginRange ?? [];
      this.rangeRowsByKey.set(cacheKey, rows);
      return rows;
    } finally {
      this.pendingByKey.delete(cacheKey);
    }
  }

  private resolveMarginFromRange(
    marginRows: ProjectStandardMargin[],
    sellAsSellPrice: number | null
  ): number {
    if (!marginRows?.length) {
      return 0;
    }

    if (sellAsSellPrice == null) {
      return 0;
    }

    const matchingRow = marginRows.find((row) => {
      const min = row.minSellPrice;
      const max = row.maxSellPrice;
      const isWithinMin = min == null || sellAsSellPrice >= min;
      const isWithinMax = max == null || sellAsSellPrice <= max;
      return isWithinMin && isWithinMax;
    });

    return matchingRow?.margin ?? marginRows[0]?.margin ?? 0;
  }

  private buildMinimumMarginRequest(): MinimumProjectStandardMarginRequestBase | null {
    const estimateId = this.commonDataService.getEstimateId();
    if (!estimateId || estimateId === CONSTANTS.DEFAULT_ESTIMATE_ID) {
      return null;
    }

    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    const estimateSettings = estimateMetaData?.estimateSettings;
    const estimateData = this.commonDataService.getEstimateData();

    // Guard against app-load state where estimate UI is rendered before an estimate is opened.
    if (!estimateSettings || !estimateData) {
      return null;
    }

    return this.commonFunctionsService.buildMinimumProjectStandardMarginRequest(
      this.commonDataService.getEstimateCultureId(),
      estimateSettings.verticalMarketId ?? CONSTANTS.ZERO,
      estimateData.profitCenterId ?? CONSTANTS.ZERO,
      estimateSettings.subSegmentId ?? CONSTANTS.ZERO,
      estimateSettings.businessUnitId ?? CONSTANTS.ZERO,
      estimateSettings.salesType ?? CONSTANTS.ZERO,
      estimateSettings.systemType ?? CONSTANTS.ZERO
    );
  }

  private getCacheKey(request: MinimumProjectStandardMarginRequestBase): string {
    return [
      request.cultureId,
      request.verticalMarketId,
      request.segmentId,
      request.subSegmentId,
      request.systemOrSalesTypeId
    ].join('|');
  }

  getSystemOrSalestypeId(): number {
    const estimateSettings = this.commonDataService.getEstimateMetaData()?.estimateSettings;
    return this.commonFunctionsService.getSystemOrSalesTypeIdForMinimumMargin(
      estimateSettings?.businessUnitId ?? 0,
      this.commonDataService.getEstimateCultureId(),
      estimateSettings?.salesType ?? 0,
      estimateSettings?.systemType ?? 0
    );
  }

  isSalesTypeApplicable(): boolean {
    const businessUnitId = this.commonDataService.getEstimateMetaData()?.estimateSettings?.businessUnitId;
    return this.commonFunctionsService.isSalesTypeApplicableForMinimumMargin(
      businessUnitId ?? 0,
      this.commonDataService.getEstimateCultureId()
    );
  }
}
